import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Search, X } from "lucide-react";

import { LeadAvatar } from "@/components/alia/lead-avatar";
import { ChannelBadge } from "@/components/alia/status-pill";
import { cn, formatDebtCompact, STAGE_LABELS } from "@/lib/utils";
import type { Conversation } from "@/types";

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  conversations: Conversation[];
}

export function CommandPalette({
  open,
  onClose,
  conversations,
}: CommandPaletteProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  // Focus input when opening, reset state when closing
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Filtered results — supports `#tag` syntax to filter by tag
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations.slice(0, 8);

    // Tag-only search (e.g. "#urgente")
    if (q.startsWith("#")) {
      const tagQuery = q.slice(1);
      if (!tagQuery) return [];
      return conversations
        .filter((c) =>
          c.tags.some((t) => t.label.toLowerCase().includes(tagQuery))
        )
        .slice(0, 8);
    }

    // Regular search across name/preview/city/phone/tags
    return conversations
      .filter((c) => {
        // Coerce phone to string defensively — el backend a veces lo manda
        // como number (sin comillas) en lugar de string, y .includes() falla
        const phoneStr = c.lead.phone != null ? String(c.lead.phone) : "";
        return (
          c.lead.name.toLowerCase().includes(q) ||
          c.preview.toLowerCase().includes(q) ||
          c.lead.city?.toLowerCase().includes(q) ||
          phoneStr.includes(q) ||
          c.tags.some((t) => t.label.toLowerCase().includes(q))
        );
      })
      .slice(0, 8);
  }, [conversations, query]);

  // Keep activeIdx in bounds
  useEffect(() => {
    if (activeIdx >= results.length) setActiveIdx(0);
  }, [results.length, activeIdx]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const target = results[activeIdx];
        if (target) {
          navigate(`/inbox/${target.id}`);
          onClose();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, results, activeIdx, navigate, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh] animate-fade-in"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />

      {/* Panel */}
      <div
        className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-border-strong bg-surface shadow-2xl ring-1 ring-white/5 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-border px-4">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre, ciudad, teléfono o #tag…"
            className="h-12 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
          />
          <button
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-surface-2 hover:text-foreground"
            aria-label="Cerrar"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {results.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              Sin resultados para “{query}”
            </div>
          ) : (
            <ul className="space-y-0.5">
              {results.map((c, i) => (
                <li key={c.id}>
                  <button
                    onClick={() => {
                      navigate(`/inbox/${c.id}`);
                      onClose();
                    }}
                    onMouseEnter={() => setActiveIdx(i)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
                      activeIdx === i
                        ? "bg-surface-2"
                        : "hover:bg-surface-2/50"
                    )}
                  >
                    <LeadAvatar name={c.lead.name} size={32} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="truncate text-sm font-medium text-foreground">
                          {c.lead.name}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {STAGE_LABELS[c.lead.stage]}
                          {c.lead.debt && ` · ${formatDebtCompact(c.lead.debt)}`}
                        </span>
                      </div>
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {c.preview}
                      </p>
                    </div>
                    <ChannelBadge channel={c.channel} />
                    {activeIdx === i && (
                      <ArrowRight className="size-3.5 shrink-0 text-brand" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer with hotkey hints */}
        <div className="flex items-center justify-between gap-3 border-t border-border bg-surface-2/50 px-4 py-2.5 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-3">
            <Hint k="↑↓">Navegar</Hint>
            <Hint k="↵">Abrir</Hint>
            <Hint k="esc">Cerrar</Hint>
          </div>
          <span className="text-muted-foreground/70">
            {results.length} {results.length === 1 ? "resultado" : "resultados"}
          </span>
        </div>
      </div>
    </div>
  );
}

function Hint({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-surface px-1 font-mono text-[10px] font-medium text-muted-foreground">
        {k}
      </kbd>
      {children}
    </span>
  );
}
